import React, { useMemo, useState, useCallback } from "react";
import { Dimensions, View, LayoutChangeEvent } from "react-native";
import styled, { useTheme } from "styled-components/native";
import { MotiView } from "moti";
import { Skeleton } from "moti/skeleton";
import { SpaceType } from "@components/atoms/Spacer/types";
import { getNumOfColumns } from "@scenes/searchResults/components/utils";
import { SearchResultsAndFavoritesEnum } from "@utils/product/searchResultConstants";

const PulseAnimation = {
  from: { opacity: 0.6 },
  to: { opacity: 1 },
};

interface GridSkeletonProps {
  rows?: number;
  isFilterVisible?: boolean;
}

const GridViewSkeleton = ({
  rows = 2,
  isFilterVisible = false,
}: GridSkeletonProps) => {
  const theme = useTheme();
  const screenWidth = Dimensions.get("window").width;
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  const handleLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const width = e.nativeEvent.layout.width;
      if (width && width !== containerWidth) setContainerWidth(width);
    },
    [containerWidth]
  );

  const effectiveWidth =
    containerWidth ||
    (theme.isLargeScreen ? 1552 + (!isFilterVisible ? 320 : 0) : screenWidth);

  const columns = useMemo(
    () =>
      getNumOfColumns(
        SearchResultsAndFavoritesEnum.GRID,
        effectiveWidth,
        theme
      ),
    [effectiveWidth, theme]
  );

  rows = 2;

  const GAP = SpaceType.Small;
  const SIDE_PADDING = SpaceType.Basic * 2;
  const totalSpacing = SIDE_PADDING + GAP * (columns - 1);
  const cardWidth = Math.floor((effectiveWidth - totalSpacing) / columns);
  const totalItems = columns * rows;

  return (
    <Wrapper
      gap={GAP}
      padding={SIDE_PADDING / 2}
      onLayout={handleLayout}
      style={{
        width: theme.isLargeScreen ? "100%" : undefined,
        alignSelf: theme.isLargeScreen ? "center" : undefined,
      }}
    >
      {Array.from({ length: totalItems }).map((_, i) => (
        <Card key={i} style={{ width: cardWidth }}>
          <ImagePlaceholder
            from={PulseAnimation.from}
            animate={PulseAnimation.to}
            transition={{ loop: true, repeat: Infinity, duration: 1000 }}
          />
          <Content>
            <Skeleton colorMode="light" width={"100%"} height={14} radius={4} />
            <Spacer size={SpaceType.Small} />
            <Skeleton colorMode="light" width={"100%"} height={14} radius={4} />
            <Spacer size={SpaceType.Small} />
            <BottomRow>
              <Skeleton
                colorMode="light"
                width={theme.isMobile ? "85%" : "90%"}
                height={16}
                radius={4}
              />
              <Skeleton colorMode="light" width={20} height={20} radius={10} />
            </BottomRow>
          </Content>
        </Card>
      ))}
    </Wrapper>
  );
};

export default GridViewSkeleton;

const Wrapper = styled(View)<{ gap: number; padding: number }>`
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${({ gap }) => gap}px;
  padding: ${({ padding }) => padding}px;
`;

const Card = styled(View)`
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: ${SpaceType.Small}px;
  overflow: hidden;
  border-width: 1px;
  border-color: ${({ theme }) => theme["core-03"]};
`;

const ImagePlaceholder = styled(MotiView)`
  width: 100%;
  aspect-ratio: 1;
  background-color: ${({ theme }) => theme["core-02"]};
`;

const Content = styled(View)`
  padding: ${SpaceType.ExtraSmall}px;
`;

const Spacer = styled(View)<{ size: number }>`
  height: ${({ size }) => size}px;
`;

const BottomRow = styled(View)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-top: ${SpaceType.Tiny}px;
`;
